import type { ScopeSectionSchema } from './types'
import { AML_RELATIONSHIPS, AML_VENDORS } from '../types'

export const amlSections: ScopeSectionSchema[] = [
  {
    id: 'aml_core_setup',
    tabId: 'aml',
    formTitle: 'AML Checks',
    formDescription: 'AML screening configuration and vendor details.',
    pdfTitle: 'AML - Core setup',
    pdfDescription: 'High-level AML screening decisions',
    highLevelSummary: 'AML check level and core screening decisions.',
    groups: [
      {
        id: 'aml_tab_header',
        tabId: 'aml',
        sectionId: 'aml_core_setup',
        formTitle: 'AML Configuration',
        formDescription: 'Anti-Money Laundering screening and monitoring settings',
        pdfTitle: 'AML Configuration',
        pdfDescription: 'Anti-Money Laundering screening and monitoring settings',
      },
      {
        id: 'aml_check_level_group',
        tabId: 'aml',
        sectionId: 'aml_core_setup',
        formTitle: 'AML Check Level',
        formDescription: 'Choose the overall AML screening depth required.',
        tooltip: 'AML check level guidance.',
        pdfTitle: 'AML Check Level',
        pdfDescription: 'Overall AML screening depth.',
      },
      {
        id: 'aml_vendor_group',
        tabId: 'aml',
        sectionId: 'aml_core_setup',
        formTitle: 'AML Vendor',
        formDescription: 'Select the primary vendor used for AML screening.',
        helperText: 'Required for completion.',
        pdfTitle: 'AML Vendor',
        pdfDescription: 'Primary AML screening vendor.',
      },
    ],
    fields: [
      {
        id: 'aml_level',
        tabId: 'aml',
        sectionId: 'aml_core_setup',
        groupId: 'aml_check_level_group',
        path: 'amlConfig.level',
        valueType: 'select',
        formLabel: 'AML Check Level',
        formDescription: 'Choose the overall AML screening depth required.',
        tooltip: 'Select Standard, Enhanced, Not applicable, or Don\'t know.',
        pdfLabel: 'AML check level',
        pdfDescription: 'Confirm whether AML screening is required.',
        contributesToCompletion: true,
        options: [
          { value: 'standard', label: 'Standard AML Checks' },
          { value: 'enhanced', label: 'Enhanced AML Checks' },
          { value: 'na', label: 'Not Applicable' },
          { value: 'unknown', label: 'Don\'t know' },
        ],
      },
      {
        id: 'aml_coverage',
        tabId: 'aml',
        sectionId: 'aml_core_setup',
        groupId: 'aml_check_level_group',
        path: 'amlConfig.level',
        valueType: 'text',
        formLabel: 'AML coverage',
        pdfLabel: 'AML coverage',
        pdfDescription: 'Derived from AML check level (standard vs enhanced).',
        contributesToCompletion: false,
      },
      {
        id: 'aml_vendor',
        tabId: 'aml',
        sectionId: 'aml_core_setup',
        groupId: 'aml_vendor_group',
        path: 'amlConfig.vendor',
        valueType: 'select',
        formLabel: 'Primary Vendor',
        formDescription: 'Select the primary vendor used for AML screening.',
        pdfLabel: 'AML vendor',
        pdfDescription: 'Select the primary AML vendor (or choose Other and specify).',
        contributesToCompletion: true,
        options: AML_VENDORS.map((v) => ({ value: v.id, label: v.label })),
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
      {
        id: 'aml_other_vendor',
        tabId: 'aml',
        sectionId: 'aml_core_setup',
        groupId: 'aml_vendor_group',
        path: 'amlConfig.otherVendor',
        valueType: 'text',
        formLabel: 'Other Vendor Name',
        formDescription: 'If "Other" is selected above, specify the vendor name.',
        placeholder: 'Enter vendor name',
        pdfLabel: 'Other AML vendor',
        pdfDescription: 'Other AML vendor name (when applicable).',
        contributesToCompletion: false,
        visibleWhen: [
          { dependsOn: 'amlConfig.level', notEquals: 'na' },
          { dependsOn: 'amlConfig.vendor', equals: 'other' },
        ],
      },
    ],
  },
  {
    id: 'aml_relationship_vendor',
    tabId: 'advanced',
    formTitle: 'Relationship',
    formDescription: 'Vendor relationship model',
    pdfTitle: 'AML - Relationship',
    pdfDescription: 'Vendor relationship model',
    highLevelSummary: 'AML vendor relationship model.',
    groups: [
      {
        id: 'aml_relationship_group',
        tabId: 'advanced',
        sectionId: 'aml_relationship_vendor',
        formTitle: 'AML Relationship',
        formDescription: 'How will the AML vendor relationship be managed?',
        helperText: 'Required for completion.',
        pdfTitle: 'AML Relationship',
        pdfDescription: 'AML vendor relationship management model.',
      },
    ],
    fields: [
      {
        id: 'aml_relationship',
        tabId: 'advanced',
        sectionId: 'aml_relationship_vendor',
        groupId: 'aml_relationship_group',
        path: 'amlConfig.relationship',
        valueType: 'select',
        formLabel: 'Relationship',
        formDescription: 'How will the AML vendor relationship be managed?',
        pdfLabel: 'AML relationship',
        pdfDescription: 'Select whether AML is via FrankieOne or direct with vendor.',
        contributesToCompletion: true,
        options: AML_RELATIONSHIPS.map((r) => ({ value: r.id, label: r.label })),
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
    ],
  },
  {
    id: 'aml_screening_settings',
    tabId: 'aml',
    formTitle: 'Screening Settings',
    formDescription: 'Which entities to screen and how monitoring should work.',
    pdfTitle: 'AML - Screening settings',
    pdfDescription: 'PEP/sanctions screening behavior and matching thresholds',
    highLevelSummary: 'AML screening settings (PEP, monitoring, fuzziness).',
    groups: [
      {
        id: 'aml_pep_levels_group',
        tabId: 'aml',
        sectionId: 'aml_screening_settings',
        formTitle: 'PEP Screening Levels',
        formDescription: 'Select which Politically Exposed Person levels to screen.',
        tooltip: 'PEP levels guidance.',
        pdfTitle: 'PEP levels',
        pdfDescription: 'Selected PEP screening levels.',
      },
      {
        id: 'aml_monitoring_type_group',
        tabId: 'aml',
        sectionId: 'aml_screening_settings',
        formTitle: 'Monitoring Type',
        formDescription: 'Choose whether screening is a one-off check or ongoing monitoring (typically daily).',
        tooltip: 'Monitoring type guidance.',
        pdfTitle: 'Monitoring type',
        pdfDescription: 'One-time vs ongoing monitoring.',
      },
      {
        id: 'aml_fuzziness_group',
        tabId: 'advanced',
        sectionId: 'aml_screening_settings',
        formTitle: 'Name Matching Fuzziness',
        formDescription:
          'How strict should name matching be? Lower values = fewer false positives, higher values = fewer missed matches',
        tooltip: 'Name matching fuzziness guidance.',
        pdfTitle: 'Name matching fuzziness',
        pdfDescription: 'Name matching fuzziness / tolerance.',
      },
      {
        id: 'aml_pep_query_enrichment_group',
        tabId: 'advanced',
        sectionId: 'aml_screening_settings',
        formTitle: 'PEP screening query enrichment',
        formDescription:
          'Whether to include country information in the screening search query (accuracy vs. comprehensiveness trade-off).',
        tooltip: 'PEP screening query enrichment guidance.',
        pdfTitle: 'PEP query enrichment',
        pdfDescription: 'Country enrichment for PEP screening queries.',
      },
      {
        id: 'aml_batch_processing_group',
        tabId: 'advanced',
        sectionId: 'aml_screening_settings',
        formTitle: 'Batch Processing',
        formDescription: 'Does the customer require batch processing capability for AML screening?',
        tooltip: 'Batch processing requirements.',
        pdfTitle: 'Batch processing',
        pdfDescription: 'Batch processing requirements and details.',
      },
      {
        id: 'aml_jurisdiction_screening_group',
        tabId: 'aml',
        sectionId: 'aml_screening_settings',
        formTitle: 'Jurisdiction Screening',
        formDescription: 'Should screening checks be performed against every jurisdiction?',
        tooltip: 'Jurisdiction screening preferences.',
        pdfTitle: 'Jurisdiction screening',
        pdfDescription: 'Jurisdiction screening requirements.',
      },
    ],
    fields: [
      {
        id: 'aml_pep_levels',
        tabId: 'aml',
        sectionId: 'aml_screening_settings',
        groupId: 'aml_pep_levels_group',
        path: 'amlConfig.pepLevels',
        valueType: 'multi_select',
        formLabel: 'PEP Levels',
        formDescription: 'Select which Politically Exposed Person levels to screen.',
        tooltip: 'PEP levels guidance.',
        pdfLabel: 'PEP levels',
        pdfDescription: 'Select one or more PEP levels to screen.',
        contributesToCompletion: true,
        options: [
          { value: '1', label: 'Level 1 - High Risk' },
          { value: '2', label: 'Level 2 - Medium Risk' },
          { value: '3', label: 'Level 3 - Low Risk' },
          { value: '4', label: 'Level 4 - Lower Risk' },
        ],
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
      {
        id: 'aml_monitoring_type',
        tabId: 'aml',
        sectionId: 'aml_screening_settings',
        groupId: 'aml_monitoring_type_group',
        path: 'amlConfig.monitoringType',
        valueType: 'select',
        formLabel: 'Monitoring Type',
        formDescription: 'Choose whether screening is a one-off check or ongoing monitoring (typically daily).',
        tooltip: 'Monitoring type guidance.',
        pdfLabel: 'Monitoring type',
        pdfDescription: 'Choose one-time screening vs ongoing monitoring.',
        contributesToCompletion: true,
        options: [
          { value: 'one-time', label: 'One-time Check' },
          { value: 'ongoing', label: 'Ongoing Monitoring' },
          { value: 'unknown', label: "Don't know" },
        ],
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
      {
        id: 'aml_fuzziness',
        tabId: 'advanced',
        sectionId: 'aml_screening_settings',
        groupId: 'aml_fuzziness_group',
        path: 'amlConfig.fuzziness',
        valueType: 'number',
        formLabel: 'Name Matching Fuzziness',
        formDescription:
          'How strict should name matching be? Lower values = fewer false positives, higher values = fewer missed matches',
        tooltip: 'Name matching fuzziness guidance.',
        pdfLabel: 'Name matching fuzziness',
        pdfDescription: 'Name matching fuzziness / tolerance.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
      {
        id: 'aml_send_countries_for_pep',
        tabId: 'advanced',
        sectionId: 'aml_screening_settings',
        groupId: 'aml_pep_query_enrichment_group',
        path: 'amlConfig.sendCountriesForPep',
        valueType: 'tri_state',
        formLabel: 'Send countries for PEP',
        formDescription: 'Whether to include country information in PEP screening queries.',
        pdfLabel: 'Send countries for PEP',
        pdfDescription: 'Whether to include country information in PEP screening queries.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
      {
        id: 'aml_batch_processing',
        tabId: 'advanced',
        sectionId: 'aml_screening_settings',
        groupId: 'aml_batch_processing_group',
        path: 'amlConfig.batchProcessing.required',
        valueType: 'select',
        formLabel: 'Batch Processing',
        formDescription: 'Does the customer require batch processing capability for AML screening?',
        tooltip: 'Batch processing requirements for bulk screening operations.',
        pdfLabel: 'Batch processing required',
        pdfDescription: 'Whether batch processing is required for AML checks.',
        contributesToCompletion: false,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: 'not_required', label: 'Not required' },
          { value: 'required_regularly', label: 'Required regularly' },
          { value: 'required_once', label: 'Required once' },
        ],
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
      {
        id: 'aml_batch_processing_details',
        tabId: 'advanced',
        sectionId: 'aml_screening_settings',
        groupId: 'aml_batch_processing_group',
        path: 'amlConfig.batchProcessing.details',
        valueType: 'textarea',
        formLabel: 'Batch Processing Details',
        formDescription: 'Provide additional details about batch processing requirements.',
        placeholder: 'e.g., Expected volume, frequency, file format preferences...',
        pdfLabel: 'Batch processing details',
        pdfDescription: 'Additional batch processing requirements.',
        contributesToCompletion: false,
        visibleWhen: [
          { dependsOn: 'amlConfig.level', notEquals: 'na' },
          { dependsOn: 'amlConfig.batchProcessing.required', notEquals: 'not_required' },
          { dependsOn: 'amlConfig.batchProcessing.required', notEquals: 'unknown' },
        ],
      },
      {
        id: 'aml_jurisdiction_screening_all',
        tabId: 'aml',
        sectionId: 'aml_screening_settings',
        groupId: 'aml_jurisdiction_screening_group',
        path: 'amlConfig.jurisdictionScreening.screenAllJurisdictions',
        valueType: 'tri_state',
        formLabel: 'Screen Against All Jurisdictions',
        formDescription: 'Should screening checks be performed against every jurisdiction?',
        tooltip: 'Whether to screen against all available jurisdictions or specific ones.',
        pdfLabel: 'Screen all jurisdictions',
        pdfDescription: 'Whether to screen against all jurisdictions or specific ones.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
      {
        id: 'aml_jurisdiction_screening_specific',
        tabId: 'aml',
        sectionId: 'aml_screening_settings',
        groupId: 'aml_jurisdiction_screening_group',
        path: 'amlConfig.jurisdictionScreening.specificJurisdictions',
        valueType: 'textarea',
        formLabel: 'Specific Jurisdictions',
        formDescription: 'List the specific jurisdictions to screen against.',
        placeholder: 'e.g., OFAC (US), EU, UN, UK HMT, DFAT (Australia)...',
        pdfLabel: 'Specific jurisdictions',
        pdfDescription: 'Jurisdictions to screen against.',
        contributesToCompletion: false,
        visibleWhen: [
          { dependsOn: 'amlConfig.level', notEquals: 'na' },
          { dependsOn: 'amlConfig.jurisdictionScreening.screenAllJurisdictions', equals: 'no' },
        ],
      },
      {
        id: 'aml_screening_entity_types_summary',
        tabId: 'aml',
        sectionId: 'aml_screening_settings',
        groupId: 'aml_pep_levels_group',
        path: 'amlConfig.screeningEntityTypes',
        valueType: 'text',
        formLabel: 'Entity types screened (summary)',
        pdfLabel: 'Entity types screened',
        pdfDescription: 'Confirm which entity types should be screened (at minimum, Individuals is typical).',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
    ],
  },
  {
    id: 'aml_custom_configuration',
    tabId: 'advanced',
    formTitle: 'Custom configuration',
    formDescription: 'Customer-specific lists beyond standard coverage',
    pdfTitle: 'AML - Custom configuration',
    pdfDescription: 'Customer-specific lists beyond standard coverage',
    highLevelSummary: 'Custom AML lists (sanctions/watchlists).',
    groups: [
      {
        id: 'aml_custom_configuration_group',
        tabId: 'advanced',
        sectionId: 'aml_custom_configuration',
        formTitle: 'Custom Configuration (Optional)',
        pdfTitle: 'Custom configuration',
        pdfDescription: 'Customer-specific AML lists.',
      },
    ],
    fields: [
      {
        id: 'aml_custom_block_list',
        tabId: 'advanced',
        sectionId: 'aml_custom_configuration',
        groupId: 'aml_custom_configuration_group',
        path: 'amlConfig.customBlockList',
        valueType: 'textarea',
        formLabel: 'Custom Block List',
        formDescription: 'Specify any custom sanctions lists or watchlists the customer requires beyond standard coverage.',
        placeholder: 'Enter custom sanctions lists, watchlists, or other blocking criteria...',
        pdfLabel: 'Custom block list',
        pdfDescription: 'Customer-specific sanctions/watchlists.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
    ],
  },

  {
    id: 'aml_screening_entity_types',
    tabId: 'aml',
    formTitle: 'Screening Entity Types',
    formDescription: 'What entity types does the customer need to screen?',
    pdfTitle: 'AML - Screening entity types',
    pdfDescription: 'Entity types to screen in AML.',
    highLevelSummary: 'AML screening entity types (individuals, businesses).',
    groups: [
      {
        id: 'aml_screening_entity_types_group',
        tabId: 'aml',
        sectionId: 'aml_screening_entity_types',
        formTitle: 'Screening Entity Types',
        formDescription: 'What entity types does the customer need to screen?',
        tooltip:
          "This drives implementation scope and which entity profiles must be supported. If unsure, choose Don't know.",
        pdfTitle: 'Screening entity types',
        pdfDescription: 'Entity types to screen.',
      },
    ],
    fields: [
      {
        id: 'aml_screening_entity_individuals',
        tabId: 'aml',
        sectionId: 'aml_screening_entity_types',
        groupId: 'aml_screening_entity_types_group',
        path: 'amlConfig.screeningEntityTypes.individuals',
        valueType: 'tri_state',
        formLabel: 'Individuals',
        formDescription: 'Natural persons (customers/beneficiaries).',
        pdfLabel: 'Individuals screening',
        pdfDescription: 'Whether individuals must be screened.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
      {
        id: 'aml_screening_entity_businesses',
        tabId: 'aml',
        sectionId: 'aml_screening_entity_types',
        groupId: 'aml_screening_entity_types_group',
        path: 'amlConfig.screeningEntityTypes.businesses',
        valueType: 'tri_state',
        formLabel: 'Businesses',
        formDescription: 'Companies / legal entities.',
        pdfLabel: 'Businesses screening',
        pdfDescription: 'Whether businesses must be screened.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
    ],
  },

  {
    id: 'aml_pep_screening_configuration',
    tabId: 'aml',
    formTitle: 'PEP Screening Configuration',
    formDescription: 'More detailed PEP configuration (beyond levels).',
    pdfTitle: 'AML - PEP configuration',
    pdfDescription: 'Detailed PEP screening configuration.',
    highLevelSummary: 'PEP screening configuration details.',
    groups: [
      {
        id: 'aml_pep_screening_configuration_group',
        tabId: 'aml',
        sectionId: 'aml_pep_screening_configuration',
        formTitle: 'PEP Screening Configuration',
        formDescription: 'More detailed PEP configuration (beyond levels).',
        tooltip:
          'Use this when the customer has requirements beyond selecting PEP levels (e.g. family members).',
        pdfTitle: 'PEP screening configuration',
        pdfDescription: 'Detailed PEP screening configuration.',
      },
    ],
    fields: [
      {
        id: 'aml_pep_include_family_members',
        tabId: 'aml',
        sectionId: 'aml_pep_screening_configuration',
        groupId: 'aml_pep_screening_configuration_group',
        path: 'amlConfig.pepScreeningConfig.includeFamilyMembers',
        valueType: 'tri_state',
        formLabel: 'Include family members',
        formDescription: 'Include PEP family members where supported by the vendor.',
        pdfLabel: 'Include family members',
        pdfDescription: 'Include PEP family members where supported by vendor.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
      {
        id: 'aml_pep_include_historical_peps',
        tabId: 'aml',
        sectionId: 'aml_pep_screening_configuration',
        groupId: 'aml_pep_screening_configuration_group',
        path: 'amlConfig.pepScreeningConfig.includeHistoricalPeps',
        valueType: 'tri_state',
        formLabel: 'Include historical PEPs',
        formDescription: 'Screen for people who were previously PEPs (where supported).',
        pdfLabel: 'Include historical PEPs',
        pdfDescription: 'Include historical PEPs where supported by vendor.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
    ],
  },

  {
    id: 'aml_sanctions_screening_configuration',
    tabId: 'aml',
    formTitle: 'Sanctions Screening Configuration',
    formDescription: 'Enable sanctions screening and choose high-level handling.',
    pdfTitle: 'AML - Sanctions configuration',
    pdfDescription: 'Sanctions screening configuration.',
    highLevelSummary: 'Sanctions match action handling.',
    groups: [
      {
        id: 'aml_sanctions_screening_group',
        tabId: 'aml',
        sectionId: 'aml_sanctions_screening_configuration',
        formTitle: 'Sanctions Screening Configuration',
        formDescription: 'Enable sanctions screening and choose high-level handling.',
        tooltip: 'Sanctions hits often require strict actions. If unsure, set match action to TBD.',
        pdfTitle: 'Sanctions screening configuration',
        pdfDescription: 'Sanctions screening configuration.',
      },
    ],
    fields: [
      {
        id: 'aml_sanctions_match_action',
        tabId: 'aml',
        sectionId: 'aml_sanctions_screening_configuration',
        groupId: 'aml_sanctions_screening_group',
        path: 'amlConfig.sanctionsScreeningConfig.matchAction',
        valueType: 'select',
        formLabel: 'Match Action',
        formDescription: 'How should sanctions matches be handled?',
        pdfLabel: 'Sanctions match action',
        pdfDescription: 'Handling of sanctions matches.',
        contributesToCompletion: false,
        options: [
          { value: 'tbd', label: 'TBD' },
          { value: 'automatic_fail', label: 'Automatic Fail' },
          { value: 'manual_review_required', label: 'Manual Review Required' },
          { value: 'alert_only', label: 'Alert Only' },
        ],
        visibleWhen: [{ dependsOn: 'amlConfig.level', notEquals: 'na' }],
      },
    ],
  },

  {
    id: 'aml_adverse_media_configuration',
    tabId: 'aml',
    formTitle: 'Adverse Media Configuration',
    formDescription: 'Enable adverse media screening and choose source tier.',
    pdfTitle: 'AML - Adverse media configuration',
    pdfDescription: 'Adverse media screening configuration.',
    highLevelSummary: 'Adverse media settings (sources and match action).',
    groups: [
      {
        id: 'aml_adverse_media_group',
        tabId: 'aml',
        sectionId: 'aml_adverse_media_configuration',
        formTitle: 'Adverse Media Configuration',
        formDescription: 'Enable adverse media screening and choose source tier.',
        tooltip:
          'Adverse media can add cost and increase false positives. Use Standard/Premium based on customer needs.',
        pdfTitle: 'Adverse media configuration',
        pdfDescription: 'Adverse media screening configuration.',
      },
    ],
    fields: [
      {
        id: 'aml_adverse_media_category',
        tabId: 'aml',
        sectionId: 'aml_adverse_media_configuration',
        groupId: 'aml_adverse_media_group',
        path: 'amlConfig.adverseMediaScreeningConfig.category',
        valueType: 'select',
        formLabel: 'Category',
        formDescription: 'Which adverse media category tier should be used?',
        pdfLabel: 'Adverse media category',
        pdfDescription: 'Adverse media category tier.',
        contributesToCompletion: false,
        options: [
          { value: 'tbd', label: 'TBD' },
          { value: 'standard', label: 'Standard' },
          { value: 'premium', label: 'Premium' },
          { value: 'both', label: 'Both' },
        ],
        visibleWhen: [{ dependsOn: 'amlConfig.level', equals: 'enhanced' }],
      },
      {
        id: 'aml_adverse_media_match_action',
        tabId: 'aml',
        sectionId: 'aml_adverse_media_configuration',
        groupId: 'aml_adverse_media_group',
        path: 'amlConfig.adverseMediaScreeningConfig.matchAction',
        valueType: 'select',
        formLabel: 'Match Action',
        formDescription: 'How should adverse media matches be handled?',
        pdfLabel: 'Adverse media match action',
        pdfDescription: 'Handling of adverse media matches.',
        contributesToCompletion: false,
        options: [
          { value: 'tbd', label: 'TBD' },
          { value: 'manual_review_required', label: 'Manual Review Required' },
          { value: 'alert_only', label: 'Alert Only' },
          { value: 'risk_score_impact', label: 'Risk Score Impact' },
        ],
        visibleWhen: [{ dependsOn: 'amlConfig.level', equals: 'enhanced' }],
      },
    ],
  },

  {
    id: 'aml_additional_watchlists',
    tabId: 'aml',
    formTitle: 'Additional Watchlists',
    formDescription: 'Extra list types beyond standard sanctions/PEP/adverse media.',
    pdfTitle: 'AML - Additional watchlists',
    pdfDescription: 'Additional watchlist types.',
    highLevelSummary: 'Additional watchlists (types and enablement).',
    groups: [
      {
        id: 'aml_additional_watchlists_group',
        tabId: 'aml',
        sectionId: 'aml_additional_watchlists',
        formTitle: 'Additional Watchlists',
        formDescription: 'Extra list types beyond standard sanctions/PEP/adverse media.',
        tooltip:
          "These lists can be highly customer-specific (e.g. disqualified directors). If unsure, choose Don't know for enablement and leave types unselected.",
        pdfTitle: 'Additional watchlists',
        pdfDescription: 'Additional watchlist types.',
      },
    ],
    fields: [
      {
        id: 'aml_additional_watchlists_types',
        tabId: 'aml',
        sectionId: 'aml_additional_watchlists',
        groupId: 'aml_additional_watchlists_group',
        path: 'amlConfig.additionalWatchlistsConfig.types',
        valueType: 'multi_select',
        formLabel: 'Watchlist Types',
        formDescription: 'Select additional watchlist types required.',
        pdfLabel: 'Additional watchlists',
        pdfDescription: 'Additional watchlist types to include.',
        contributesToCompletion: false,
        options: [
          { value: 'law_enforcement', label: 'Law enforcement' },
          { value: 'regulatory_enforcement', label: 'Regulatory enforcement' },
          { value: 'disqualified_directors', label: 'Disqualified directors' },
          { value: 'insolvency', label: 'Insolvency' },
          { value: 'financial_crime', label: 'Financial crime' },
          { value: 'country_specific', label: 'Country-specific' },
          { value: 'all', label: 'All' },
        ],
        visibleWhen: [{ dependsOn: 'amlConfig.level', equals: 'enhanced' }],
      },
      {
        id: 'aml_aging_enabled',
        tabId: 'advanced',
        sectionId: 'aml_screening_requirements',
        groupId: 'aml_aging_group',
        path: 'amlConfig.agingEnabled',
        valueType: 'boolean',
        formLabel: 'Use Custom Aging Period',
        formDescription: 'Enable custom aging configuration for AML data.',
        pdfLabel: 'Custom aging enabled',
        pdfDescription: 'Whether custom aging period is configured for AML screening results.',
        contributesToCompletion: false,
      },
      {
        id: 'aml_aging_period_months',
        tabId: 'advanced',
        sectionId: 'aml_screening_requirements',
        groupId: 'aml_aging_group',
        path: 'amlConfig.agingPeriodMonths',
        valueType: 'number',
        formLabel: 'Aging Period (Months)',
        formDescription: 'Number of months before AML screening results require re-screening.',
        pdfLabel: 'Aging period',
        pdfDescription:
          'AML screening results older than this period (in months) will require re-screening. Default is 12 months (1 year). The aging system evaluates process results based on their creation dates and discards those exceeding the configured age threshold.',
        contributesToCompletion: false,
      },
      {
        id: 'aml_aging_custom_config',
        tabId: 'advanced',
        sectionId: 'aml_screening_requirements',
        groupId: 'aml_aging_group',
        path: 'amlConfig.agingCustomConfig',
        valueType: 'textarea',
        formLabel: 'Additional Aging Configuration',
        formDescription: 'Custom aging rules or specific requirements for different check types.',
        placeholder: 'e.g., Different aging periods for PEP vs Sanctions vs Adverse Media checks',
        pdfLabel: 'Custom aging configuration',
        pdfDescription: 'Special aging rules or requirements for specific check types (e.g., PEP, Sanctions, Adverse Media).',
        contributesToCompletion: false,
      },
    ],
  },
]
